import { Ref, ref, ComputedRef, computed } from 'vue'
import { Category } from '@/types/category'
import { getCategories } from '@/api/category/getCategories'
import { deleteCategory } from '@/api/category/deleteCategory'
import { createCategory } from '@/api/category/createCategory'
import { updateCategory } from '@/api/category/updateCategory'
import { useLoading, Loading } from '@/composable/useLoading'

interface UseCategory {
  data: Ref<Category[]>
  fetchAll: () => Promise<Category[]>
  remove: (uuid: string) => Promise<null>
  add: (body: Category) => Promise<Category>
  update: (body: Category) => Promise<Category>
  loading: ComputedRef<Loading>
}

export function useCategory(
  initFetchAll?: boolean,
  includeDeleted?: boolean
): UseCategory {
  const data = ref<Category[]>([])
  const { state } = useLoading()

  async function fetchAll() {
    state.getAll = true
    return new Promise<Category[]>((resolve, reject) => {
      getCategories(includeDeleted)
        .then(({ data: category }) => {
          data.value = category
          resolve(category)
        })
        .catch(reject)
        .finally(() => {
          state.getAll = false
        })
    })
  }

  async function remove(uuid: string) {
    state.delete = true
    return new Promise<null>((resolve, reject) => {
      deleteCategory(uuid)
        .then(() => {
          data.value = data.value.filter((item) => item.uuid !== uuid)
          resolve(null)
        })
        .catch(reject)
        .finally(() => {
          state.delete = false
        })
    })
  }

  async function add(body: Category) {
    state.create = true
    return new Promise<Category>((resolve, reject) => {
      createCategory(body)
        .then(({ data: category }) => {
          data.value.push(category)
          resolve(category)
        })
        .catch(reject)
        .finally(() => {
          state.create = false
        })
    })
  }

  async function update(body: Category) {
    state.update = true
    return new Promise<Category>((resolve, reject) => {
      updateCategory(body)
        .then(({ data: category }) => {
          data.value = data.value.map((item) => {
            if (item.uuid === category.uuid) {
              return {
                ...item,
                ...category,
              }
            }

            return item
          })
          resolve(category)
        })
        .catch(reject)
        .finally(() => {
          state.update = false
        })
    })
  }

  if (initFetchAll) {
    fetchAll()
  }

  return {
    data,
    fetchAll,
    remove,
    add,
    update,
    loading: computed(() => state),
  }
}
